import {
  defaultMonthsRangeSettings,
  monthCodeFromOffset,
  Status,
  type TStatusManualStorage,
} from "@package/grid-utils";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import cn from "mcn";
import { useState } from "react";

import { useGlobalProducts, useGridSettingsStatusMap, useProductById, useProductConfigById } from "../../../../data";
import { knownStatusOptions, sourceToGlobalPackageIcons } from "../../../market-grid/statuses/consts";
import { type SelectedFormula, useTargetCell, useInsertCurveActions, useIsRootEodSource } from "../hooks";
import { useCellEditStatus } from "../../../calcs/react";
import { TimePassed } from "../../../ui/time-passed";
import type { ProductIdentifier } from "../../../utils/formula-ast";

import { ValueDisplay } from "./ValueDisplay";
import { RowPeriod } from "./RowPeriod";

const precedentCardShellClassName = "rounded-lg bg-card ring-1 ring-outline text-foreground";
const precedentBodyTextClassName = "text-lg leading-[18px] font-normal";

type TokenDisplayProps = {
  variable: ProductIdentifier;
  row: number;
  selected: SelectedFormula | null;
  onSelect: (p: SelectedFormula | null) => void;
  isInsideRound?: boolean;
  /** Stable AST path identifying this token; used to disambiguate identical refs. */
  path: string;
};

type CellType = "manual" | "formula" | "future" | "eod" | "snapshot";

/** Same rule as column headers / cell tooltips: EOD source, not hybrid/manual edits. */
const isEodSource = (status: string, artisType?: string | null) => Status.isEod(status) || artisType === "eod";

export const TokenDisplay = (p: TokenDisplayProps) => {
  const productConfigs = useProductConfigById(p.variable.product);
  const productInfo = useProductById(p.variable.product);
  const targetCell = useTargetCell();
  const insertCurve = useInsertCurveActions();
  const [contextMenuOpen, setContextMenuOpen] = useState(false);

  const status = useGridSettingsStatusMap()?.[p.variable.product] ?? "listen";
  const isRootEodSource = useIsRootEodSource();
  const row = p.row + (p.variable.offset ?? 0);
  const isOutOfGrid = row < 0 || row > defaultMonthsRangeSettings;
  const exactConfig = productConfigs.data?.find((c) => c.offset === row);
  const exactFormula = exactConfig?.formula ?? "";
  // For rows outside the visible grid range (configs are bounded to ~72 months),
  // fall back to any available formula for the same product. Formula curves are
  // typically uniform across offsets, so this lets users keep tracing precedents
  // beyond the grid horizon. Only fall back when no config exists for this row
  // at all — a config with an empty formula means the cell is manual entry and
  // must not be classified as a formula curve.
  const fallbackFormula =
    !exactFormula && exactConfig === undefined ? (productConfigs.data?.find((c) => !!c.formula)?.formula ?? "") : "";
  const formula = exactFormula || fallbackFormula;
  const isFormulaCurve = !!fallbackFormula || !!exactFormula;
  const isSelected = p.selected?.path === p.path;
  const isManual = !formula && productInfo.data?.has_shared_cell;
  const isEodSourceCell = isEodSource(status, productInfo.data?.artis_type);
  const isSnapshot = productInfo.data?.curve_type === "snapshot";
  // EOD has no last-edit metadata, so suppress LUT/username when either the
  // precedent token is itself EOD-sourced or when the cell being traced is —
  // an EOD-sourced root means any manual edit on a referenced curve is not
  // contributing to the displayed value.
  const hideEditMetadata = isEodSourceCell || isRootEodSource;
  const tp = isEodSourceCell
    ? "eod"
    : isSnapshot
      ? "snapshot"
      : isManual
        ? "manual"
        : isFormulaCurve
          ? "formula"
          : "future";

  const cardContent = (
    <>
      <div className="flex items-center justify-between gap-4 px-3 py-2">
        <p className={cn(precedentBodyTextClassName, "font-medium text-foreground")}>{productInfo.data?.name}</p>
        <div className="flex items-center gap-2">
          {isManual && !hideEditMetadata && <ManualStatus product={p.variable.product} row={row} />}
          {isManual && !hideEditMetadata && <ManualLUT product={p.variable.product} row={row} />}
          <CellType type={tp} />
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 px-3 py-2">
        <p className={cn(precedentBodyTextClassName, "text-foreground")}>
          <RowPeriod row={row} />
        </p>
        <span className="text-foreground inline-flex items-center gap-1.5">
          {p.isInsideRound && <RoundIndicator />}
          <ValueDisplay
            product={p.variable.product}
            field={p.variable.field}
            row={row}
            hideLabel={p.variable.field === "value" || p.variable.field === "fv"}
            outOfGrid={isOutOfGrid}
          />
        </span>
      </div>
      {isSelected && (
        <div className="w-full flex items-center justify-center absolute top-full left-0 pointer-events-none">
          <div className="text-primary h-[19px] relative">
            <DownArrow />
          </div>
        </div>
      )}
    </>
  );

  return (
    <PrecedentContextMenu productId={p.variable.product} insertCurve={insertCurve} onOpenChange={setContextMenuOpen}>
      <div
        className={cn(precedentCardShellClassName, "relative", {
          "hover:ring-primary": !isSelected && !contextMenuOpen && !!formula,
          "cursor-pointer": !!formula,
          "ring-2 ring-primary": contextMenuOpen,
          "ring-primary": isSelected && !contextMenuOpen,
        })}
        onMouseEnter={() => targetCell(p.variable.product, row)}
        onClick={(event) => {
          if (isSelected) return p.onSelect(null);
          if (!formula) return;
          const cardEl = event.currentTarget;
          const cardRect = cardEl.getBoundingClientRect();
          const container = cardEl.closest<HTMLElement>("[data-formula-container]");
          const containerLeft = container?.getBoundingClientRect().left ?? 0;
          const arrowX = cardRect.left + cardRect.width / 2 - containerLeft;
          p.onSelect({ product: p.variable.product, formula, row, path: p.path, arrowX });
        }}
      >
        {cardContent}
      </div>
    </PrecedentContextMenu>
  );
};

const menuItemClassName =
  "flex w-full items-center px-[9px] py-[4.5px] text-left text-md bg-card text-foreground hover:bg-[var(--neutral-tonal)] cursor-pointer outline-none";

const menuContentClassName = "z-[9999] min-w-[180px] py-[6px] rounded-md bg-card border border-outline";

const menuShadow = { boxShadow: "1px 4px 8px rgba(0, 0, 0, 0.15)" };

const PrecedentContextMenu = ({
  children,
  productId,
  insertCurve,
  onOpenChange,
}: {
  children: React.ReactNode;
  productId: string;
  insertCurve: ReturnType<typeof useInsertCurveActions>;
  onOpenChange: (open: boolean) => void;
}) => {
  if (!insertCurve) return children;

  const alreadyOnPage = insertCurve.isProductOnPage(productId);

  return (
    <ContextMenuPrimitive.Root onOpenChange={onOpenChange}>
      <ContextMenuPrimitive.Trigger asChild>{children}</ContextMenuPrimitive.Trigger>
      <ContextMenuPrimitive.Portal>
        <ContextMenuPrimitive.Content className={menuContentClassName} style={menuShadow}>
          {alreadyOnPage ? (
            <ContextMenuPrimitive.Item
              className={cn(menuItemClassName, "opacity-50 cursor-default pointer-events-none hover:bg-transparent")}
              disabled
            >
              Already on Page
            </ContextMenuPrimitive.Item>
          ) : (
            <ContextMenuPrimitive.Sub>
              <ContextMenuPrimitive.SubTrigger className={cn(menuItemClassName, "justify-between")}>
                <span>Insert Curve</span>
                <ChevronRight />
              </ContextMenuPrimitive.SubTrigger>
              <ContextMenuPrimitive.Portal>
                <ContextMenuPrimitive.SubContent className={menuContentClassName} style={menuShadow} sideOffset={2}>
                  <ContextMenuPrimitive.Item
                    className={menuItemClassName}
                    onSelect={() => insertCurve.insertNextToSelected(productId)}
                  >
                    Right of selected Curve
                  </ContextMenuPrimitive.Item>
                  <ContextMenuPrimitive.Item
                    className={menuItemClassName}
                    onSelect={() => insertCurve.insertAtEnd(productId)}
                  >
                    Right end
                  </ContextMenuPrimitive.Item>
                </ContextMenuPrimitive.SubContent>
              </ContextMenuPrimitive.Portal>
            </ContextMenuPrimitive.Sub>
          )}
        </ContextMenuPrimitive.Content>
      </ContextMenuPrimitive.Portal>
    </ContextMenuPrimitive.Root>
  );
};

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    style={{ color: "var(--neutral-on-tonal)" }}
    aria-hidden="true"
  >
    <path
      d="M10.8294 7.582C11.0569 7.81288 11.0569 8.1862 10.8294 8.41462L5.99073 13.3268C5.76331 13.5577 5.39556 13.5577 5.17056 13.3268C4.94556 13.096 4.94315 12.7226 5.17056 12.4942L9.59798 7.99954L5.17056 3.50486C4.94315 3.27398 4.94315 2.90065 5.17056 2.67224C5.39798 2.44382 5.76573 2.44136 5.99073 2.67224L10.8294 7.58446V7.582Z"
      fill="currentColor"
    />
  </svg>
);

const ManualStatus = (p: { product: string; row: number }) => {
  const global = useGlobalProducts();
  const productInfo = useProductById(p.product);
  const status = useGridSettingsStatusMap()?.[p.product] ?? "listen";
  const current = knownStatusOptions.find((x) => x.value === status);
  const gp = global.data?.find((x) => x.id === status);
  const source = gp?.globalPackageByGlobalPackage.source;
  const iconFile = source !== undefined ? sourceToGlobalPackageIcons[source] : undefined;
  const logo = iconFile ? `/icons/${iconFile}` : undefined;
  const isGlobal = Status.isGlobal(status);

  const code = monthCodeFromOffset(p.row);
  const productId = isGlobal ? status : p.product;
  const storageType = isGlobal ? "global" : Status.storageType(status);
  const showUsername = Status.isHybrid(status) || status === "listen";
  // When subscribed to a global package the value comes from an external feed,
  // not a local user edit — skip the username lookup.
  const edit = useCellEditStatus(
    !isGlobal ? productId : null,
    !isGlobal ? (storageType as TStatusManualStorage) : null,
    !isGlobal ? code : null,
  );

  if (isEodSource(status, productInfo.data?.artis_type)) return null;

  // For global packages the status value is the package ID (not in knownStatusOptions),
  // so fall back to the "listen" icon to indicate the implied receive-only status.
  const statusOption = current ?? (logo ? knownStatusOptions.find((x) => x.value === "listen") : undefined);
  if (isGlobal ? !logo : !statusOption && !edit.data?.username) return null;
  return (
    <>
      {isGlobal
        ? logo && <img src={logo} alt="source" className="w-4 h-4" />
        : showUsername &&
          edit.data?.username && (
            <span className={cn(precedentBodyTextClassName, "text-foreground")}>{edit.data.username}</span>
          )}
      {statusOption?.icon && <statusOption.icon />}
    </>
  );
};

const ManualLUT = (p: { product: string; row: number }) => {
  const productInfo = useProductById(p.product);
  const status = useGridSettingsStatusMap()?.[p.product] ?? "listen";
  const code = monthCodeFromOffset(p.row);
  const isGlobal = Status.isGlobal(status);
  const productId = isGlobal ? status : p.product;
  const storageType = isGlobal ? "global" : Status.storageType(status);
  const edit = useCellEditStatus(productId, storageType as TStatusManualStorage, code);

  if (isEodSource(status, productInfo.data?.artis_type)) return null;
  if (!edit.data?.edited_at) return null;
  return (
    <TimePassed className={cn(precedentBodyTextClassName, "text-foreground tabular-nums")} time={edit.data.edited_at} />
  );
};

const SnapshotCameraIcon = () => (
  // biome-ignore lint/a11y/noSvgWithoutTitle: decorative icon
  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="11" viewBox="1.5 2.5 13 11" fill="none">
    <path
      d="M6.40293 2.5C5.92812 2.5 5.48379 2.7308 5.21973 3.11138L4.55957 4.07143H3.125C2.22871 4.07143 1.5 4.77612 1.5 5.64286V11.9286C1.5 12.7953 2.22871 13.5 3.125 13.5H12.875C13.7713 13.5 14.5 12.7953 14.5 11.9286V5.64286C14.5 4.77612 13.7713 4.07143 12.875 4.07143H11.4404L10.7803 3.11138C10.5162 2.7308 10.0719 2.5 9.59707 2.5H6.40293ZM6.23281 3.76696C6.2709 3.71295 6.33438 3.67857 6.40293 3.67857H9.59453C9.66309 3.67857 9.72656 3.71049 9.76465 3.76696L10.6076 4.98973C10.7219 5.15424 10.9098 5.25246 11.1154 5.25246H12.875C13.0984 5.25246 13.2812 5.42924 13.2812 5.64531V11.931C13.2812 12.1471 13.0984 12.3239 12.875 12.3239L3.125 12.3214C2.90156 12.3214 2.71875 12.1446 2.71875 11.9286V5.64286C2.71875 5.42679 2.90156 5.25 3.125 5.25H4.88457C5.0877 5.25 5.27813 5.15179 5.39238 4.98728L6.23535 3.76451L6.23281 3.76696ZM8 11.1429C9.57168 11.1429 10.8438 9.91272 10.8438 8.39286C10.8438 6.87299 9.57168 5.64286 8 5.64286C6.42832 5.64286 5.15625 6.87299 5.15625 8.39286C5.15625 9.91272 6.42832 11.1429 8 11.1429ZM6.375 8.39286C6.375 7.52612 7.10371 6.82143 8 6.82143C8.89629 6.82143 9.625 7.52612 9.625 8.39286C9.625 9.2596 8.89629 9.96429 8 9.96429C7.10371 9.96429 6.375 9.2596 6.375 8.39286Z"
      fill="currentColor"
    />
  </svg>
);

const CellType = (p: { type: CellType }) => {
  if (p.type === "snapshot" || p.type === "eod") {
    return <SnapshotCameraIcon />;
  }
  if (p.type === "manual") return null;
  const color = cn({
    "text-[var(--semantic-on-success)] bg-[var(--semantic-success-fill)]": p.type === "formula",
    "bg-[var(--semantic-info-fill)] text-[var(--semantic-on-info)]": p.type === "future",
  });
  const label = p.type;
  return <p className={cn("text-[10px] font-semibold rounded-full px-2.5 py-0.5", color)}>{label}</p>;
};

const RoundIndicator = () => (
  <TooltipTrigger touch="toggle">
    <button
      type="button"
      className="text-foreground-muted hover:text-foreground transition-colors cursor-default"
      aria-label="Affected by ROUND function"
      onClick={(e) => e.stopPropagation()}
    >
      <FontAwesomeIcon icon={faCircleInfo} className="w-3 h-3" />
    </button>
    <Tooltip>This Curve segment is affected by the ROUND() function. Check Formula Viewer to see details.</Tooltip>
  </TooltipTrigger>
);

const DownArrow = () => (
  <>
    {/** biome-ignore lint/a11y/noSvgWithoutTitle: decorative arrow icon */}
    <svg className="h-full" width="15" height="19" viewBox="0 0 15 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6.65685 18.7071C7.04737 19.0976 7.68054 19.0976 8.07106 18.7071L14.435 12.3431C14.8255 11.9526 14.8255 11.3195 14.435 10.9289C14.0445 10.5384 13.4113 10.5384 13.0208 10.9289L7.36395 16.5858L1.7071 10.9289C1.31657 10.5384 0.683409 10.5384 0.292885 10.9289C-0.0976396 11.3195 -0.0976396 11.9526 0.292885 12.3431L6.65685 18.7071ZM7.36395 0L6.36395 0L6.36395 18H7.36395H8.36395L8.36395 0L7.36395 0Z"
        fill="currentColor"
      />
    </svg>
  </>
);
